import React from 'react';
import { Scale } from 'lucide-react';

const Legal = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] mb-4">
            <Scale className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Legal & Compliance
          </h1>
          <p className="text-lg text-[#5B6B7F]">
            Our commitment to regulatory standards and legal compliance
          </p>
        </div>
      </section>

      {/* Compliance & Licensing Section */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-gradient-to-b from-white to-[#FAFBFC]">
        <div className="max-w-4xl mx-auto">
          <div className="border border-gray-200 shadow-lg rounded-xl bg-white p-8 md:p-12">
            <div className="text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
                Compliance & Licensing
              </h2>
              
              <div className="space-y-4 text-[#1E3A5F]">
                <p className="text-base md:text-lg leading-relaxed">
                  CoineXus operates as a licensed Virtual Asset Service Provider (VASP) in Poland.
                </p>
                
                <div className="py-4 space-y-2">
                  <p className="font-semibold">KRS: 0001142506</p>
                  <p className="text-sm md:text-base">
                    Registered Office: Juliana Tuwima 48, Unit 11, 90-021 Łódź, Poland
                  </p>
                </div>
                
                <p className="text-base md:text-lg leading-relaxed">
                  We comply with AML and KYC regulations and perform enhanced due diligence for high-value transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Regulatory Framework</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus operates within a comprehensive regulatory framework designed to ensure the highest standards of compliance and customer protection. We work exclusively with regulated partners and licensed cryptocurrency OTC trading desks to facilitate secure and compliant transactions globally.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Licensing & Registration</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Our operations are conducted through partnerships with registered and licensed cryptocurrency service providers. We maintain compliance with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Financial Action Task Force (FATF) guidelines</li>
              <li>Anti-Money Laundering (AML) regulations</li>
              <li>Know Your Customer (KYC) requirements</li>
              <li>Counter-Terrorist Financing (CTF) protocols</li>
              <li>Local and international cryptocurrency regulations</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Anti-Money Laundering (AML) Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We maintain strict AML policies and procedures to prevent the use of our services for money laundering or terrorist financing. All transactions are subject to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Customer due diligence and identity verification</li>
              <li>Transaction monitoring and reporting</li>
              <li>Enhanced due diligence for high-risk transactions</li>
              <li>Regular compliance audits and reviews</li>
              <li>Cooperation with law enforcement and regulatory authorities</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Know Your Customer (KYC) Requirements</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              To comply with international regulations and ensure the security of all parties, we require customers to complete identity verification. This includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Government-issued identification documents</li>
              <li>Proof of address verification</li>
              <li>Source of funds documentation for large transactions</li>
              <li>Business registration documents for corporate clients</li>
              <li>Beneficial ownership disclosure where applicable</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Identification (KYC) Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We apply strict Know Your Customer (KYC) procedures to all clients before processing any transactions. The following steps are required:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Collection of valid government-issued ID (passport or national ID)</li>
              <li>Proof of address not older than 3 months (e.g., utility bill or bank statement)</li>
              <li>Screening against sanctions and PEP lists via Allpass.ai</li>
              <li>Verification of Source of Wealth when applicable</li>
            </ol>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              No transactions are permitted before successful verification.
            </p>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-[#5B6B7F]">
                <span className="font-semibold text-[#1E3A5F]">Authorized by:</span> Mohamed Oussema Sassi
              </p>
              <p className="text-[#5B6B7F]">
                <span className="font-semibold text-[#1E3A5F]">Position:</span> Co-Founder, Executive Director & MLRO
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Data Protection & Privacy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We are committed to protecting your personal information in accordance with applicable data protection laws, including GDPR and other international privacy regulations. Your data is:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Stored securely using industry-standard encryption</li>
              <li>Only accessed by authorized personnel</li>
              <li>Used solely for compliance and service provision purposes</li>
              <li>Retained only as long as legally required</li>
              <li>Never sold or shared with third parties without consent</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Transaction Limits & Reporting</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We adhere to international standards for transaction reporting and maintain appropriate limits based on regulatory requirements and risk assessment. Large transactions may be subject to additional verification procedures and mandatory reporting to relevant authorities as required by law.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Prohibited Activities</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Our services may not be used for any illegal activities, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Money laundering or terrorist financing</li>
              <li>Fraud or fraudulent transactions</li>
              <li>Sanctions evasion</li>
              <li>Unauthorized or unlicensed financial services</li>
              <li>Any activity that violates applicable laws or regulations</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Sanctions Compliance</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We comply with all applicable international sanctions programs and do not provide services to individuals or entities located in, or subject to, sanctions by the United Nations, European Union, United States, or other relevant authorities.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Risk Disclosure</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Cryptocurrency transactions involve significant risks, including price volatility, market risk, and regulatory uncertainty. Customers should:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Only invest what they can afford to lose</li>
              <li>Understand the nature and risks of cryptocurrency transactions</li>
              <li>Seek independent financial and legal advice when necessary</li>
              <li>Be aware that cryptocurrency values can fluctuate significantly</li>
              <li>Understand that transactions are generally irreversible</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Dispute Resolution</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              In the event of any disputes, we are committed to resolving them fairly and efficiently. Customers may contact our compliance team directly, and we will work to address concerns promptly while adhering to all applicable dispute resolution procedures and regulations.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Complaints & Dispute Handling</h2>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>If you have a complaint, please email <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a> with details.</li>
              <li>We acknowledge receipt within 2 business days and aim to provide a final response within 14 business days.</li>
              <li>If more time is required due to complexity, we will provide an interim update with a new timeline.</li>
              <li>Disputes will be handled in accordance with applicable Polish and EU regulations.</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Updates & Changes</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              This Legal & Compliance page may be updated periodically to reflect changes in regulations, our operations, or industry standards. Continued use of our services constitutes acceptance of any modifications. We encourage customers to review this page regularly.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Regulatory Information / Partners & Key Officers</h2>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="font-semibold text-[#1E3A5F] min-w-[140px]">KYC Provider:</span>
                  <span className="text-[#5B6B7F]">Allpass.ai</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-semibold text-[#1E3A5F] min-w-[140px]">Money Laundering Reporting Officer (MLRO):</span>
                  <span className="text-[#5B6B7F]">Mohamed Oussema Sassi</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-[#5B6B7F] italic">
              Our Money Laundering Reporting Officer (MLRO) oversees all AML/CTF compliance activities and reporting obligations.
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-[#FF6B35]/20">
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-4">Questions or Concerns?</h3>
            <p className="text-[#5B6B7F] leading-relaxed mb-6">
              If you have any questions about our legal and compliance framework, please contact our compliance team.
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:from-[#F7931E] hover:to-[#FF6B35] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Contact Compliance Team
            </a>
          </div>

          {/* Last Updated Date */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-[#5B6B7F] italic">
              Last updated: 2025-11-09
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Legal;
